import { Collapse, Space } from 'antd';
import { Fragment, PureComponent, ReactNode } from 'react';
import { TREATMENT_PLAN_TYPE, TreatmentPlan } from '../../../../../api/getTreatmentPlans';
import moment from 'moment';
import { ACTIVE_TAB } from './PPTreatmentPlan';
import TreatmentServicesTable from './TreatmentServicesTable';
import TreatmentPricing from './TreatmentPricing';
import { AntTooltip } from '../../../../common/AntTooltip';
import { getHeaderAndFooterContent } from '../../../leftPanel/DentalChart/Utils';
import { ORBIT } from '../../../../../api/constants';
import { track } from '../../../../../tracking';
import { Doctor } from '../../../../../store/types/prescription';
import { DFormEntity } from '../../../../../api/fetchDoctorGenieByOID';
import { getAddPDForms } from '../../../../../pages/utils';
import { getDoctorProfile } from '../../../../../store/idbHandler';
import DoctorSelectorPopup from '../../../../common/DoctorSelectorPopOver';
const { Panel } = Collapse;

interface Props {
    visibleItems?: Set<string>;
    treatmentPlan: TreatmentPlan;
    onVisibleItemsChange: (key?: string) => void;
    activeTab: ACTIVE_TAB;
    onEdit: (estimate: TreatmentPlan) => void;
    onActivate: (treatmentPlan: TreatmentPlan) => Promise<void>;
    treatmentBillingModalToggleHandler: (
        treatmentPlan?: TreatmentPlan,
        isForWorkDone?: boolean,
    ) => void;
    ptDetails: string;
    docProfile?: Doctor;
}

interface State {
    ptFormDets: DFormEntity[];
    isLoading?: boolean;
    isPrintChartLoading?: boolean;
}

class ActiveTabPlan extends PureComponent<Props, State> {
    state: State = { ptFormDets: [] };
    onActivateFunction = async (treatmentPlan: TreatmentPlan) => {
        const { onActivate } = this.props;
        this.setState({
            isLoading: true,
        });
        const p = await getAddPDForms();
        await onActivate(treatmentPlan);
        this.setState({
            isLoading: false,
            ptFormDets: p,
        });
        track('ON_ACTIVATE_FUNCTION_CLICK', { treatmentPlan: treatmentPlan.plan_id });
    };

    onPrint = (planId: string, docid?: string) => {
        const { visibleItems, onVisibleItemsChange } = this.props;
        const { printEstimate } = this;
        if (!visibleItems?.has(planId)) {
            onVisibleItemsChange(planId);
            setTimeout(() => printEstimate(docid), 100);
        } else {
            printEstimate(docid);
        }
        track('PRINT_ESTIMATE_CLICK', { planId: planId });
    };

    printEstimate = async (docid?: string) => {
        const { treatmentPlan, ptDetails } = this.props;
        let { docProfile } = this.props;

        if (docid && !docProfile) {
            docProfile = await getDoctorProfile(docid);
        }

        if (!docProfile) {
            return;
        }

        const { printIframe } = this;
        const { plan_id } = treatmentPlan;
        const ID = `estimate-container-${plan_id}`;
        const printWindow = document.createElement('iframe');
        const estimateContainerContent = document.getElementById(ID)?.innerHTML;
        const { headerHtml, footerHtml } = getHeaderAndFooterContent(
            docProfile,
            '',
            this.state.ptFormDets,
        );
        if (!printWindow || !estimateContainerContent) {
            return;
        }
        this.setState({
            isPrintChartLoading: true,
        });
        printWindow.srcdoc = `
            <!DOCTYPE html>
            <html lang="en">
                <head>
                    <title>Print</title>
                    <link href=${ORBIT} rel="stylesheet">
                   <style>
                    th {
                        display: none;
                    }
                    table thead tr:first-of-type {
                        background-color: rgb(244, 246, 249)
                    }
                    table thead tr:first-of-type td{
                        padding: 10px 12px;
                        border-right: 1px solid #f0f0f0;
                        border-bottom: 1px solid #f0f0f0;
                    }
                </style>
                </head>
                <body>
                    <div class="h-full w-full flex flex-col">
                        ${headerHtml}
                        <div class="flex justify-between items-center pt-8 text-13 font-700">
                            <p>
                                ${ptDetails}
                            </p>
                            <p>
                                <span>${treatmentPlan.name}</span>
                                <span>(Created on ${moment(treatmentPlan.updated_at).format(
                                    "DD MMM'YY",
                                )})</span>
                            </p>
                        </div>
                        <div class="flex-1 space-y-12 py-10">
                            ${estimateContainerContent}
                        </div>
                        ${footerHtml}
                    </div>
                </body>
            </html>
        `;
        document.body.appendChild(printWindow);
        printWindow.onload = () => {
            const pW = printWindow.contentWindow;
            if (!pW) return;
            printIframe(pW, printWindow);
        };
    };

    printIframe = (pW: Window, printWindow: HTMLIFrameElement) => {
        pW.focus();
        pW.print();
        pW.onafterprint = () => {
            document.body.removeChild(printWindow);
        };
        this.setState({
            isPrintChartLoading: false,
        });
    };

    render(): ReactNode {
        const {
            visibleItems,
            treatmentPlan,
            onVisibleItemsChange,
            activeTab,
            onEdit,
            treatmentBillingModalToggleHandler,
            docProfile,
        } = this.props;
        const { isLoading, isPrintChartLoading } = this.state;
        const { onActivateFunction, onPrint } = this;
        const { plan_id, name, updated_at, services } = treatmentPlan;

        return (
            <Space
                direction="vertical"
                className="w-full rounded-8"
                style={{
                    boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.15)',
                }}
            >
                <Collapse
                    expandIcon={() => (
                        <svg
                            className={`w-12 h-7 fill-darwin-neutral_new-800 transform ${
                                visibleItems?.has(plan_id) ? 'rotate-180 transition' : ''
                            }`}
                            viewBox="0 0 12 7"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path d="M5.56836 6.30664L0.693359 1.43164C0.439453 1.20312 0.439453 0.822266 0.693359 0.568359C0.921875 0.339844 1.30273 0.339844 1.55664 0.568359L6 5.03711L10.4434 0.59375C10.6719 0.339844 11.0527 0.339844 11.3066 0.59375C11.5352 0.822266 11.5352 1.20312 11.3066 1.43164L6.40625 6.30664C6.17773 6.56055 5.79688 6.56055 5.56836 6.30664Z" />
                        </svg>
                    )}
                    onChange={() => onVisibleItemsChange(plan_id)}
                    activeKey={visibleItems && Array.from(visibleItems)}
                >
                    <Panel
                        header={
                            <div className="text-13 flex items-center space-x-5 text-darwin-neutrals-1000 relative top-3">
                                <p className="font-700">{name}</p>
                                <p>(Created on {moment(updated_at).format("DD MMM'YY")})</p>
                            </div>
                        }
                        key={plan_id}
                        extra={
                            <div
                                className="flex items-center space-x-8 cursor-pointer"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {activeTab === TREATMENT_PLAN_TYPE.ESTIMATE && (
                                    <Fragment>
                                        <AntTooltip
                                            content="Edit Estimate"
                                            placement="bottom"
                                            getPopupContainer={() =>
                                                document.getElementById(
                                                    `edit-estimate-${plan_id}`,
                                                ) || document.body
                                            }
                                        >
                                            <button
                                                className="h-28 px-7 py-3 flex items-center space-x-6 rounded-4 bg-white border border-darwin-accent-bright-blue-800"
                                                onClick={() => onEdit(treatmentPlan)}
                                                id={`edit-estimate-${plan_id}`}
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 13 13"
                                                    className="w-12 h-11 fill-darwin-accent-bright-blue-800"
                                                >
                                                    <path d="M12.0752 1.83594L11.1846 0.945312C10.9033 0.664062 10.5049 0.5 10.1299 0.5C9.75488 0.5 9.35645 0.664062 9.0752 0.945312L1.24707 8.77344C1.17676 8.82031 1.15332 8.89062 1.12988 8.96094L0.520508 12.0312C0.473633 12.2891 0.661133 12.5 0.87207 12.5C0.895508 12.5 0.942383 12.5 0.96582 12.5L4.03613 11.8906C4.10645 11.8672 4.17676 11.8438 4.24707 11.7734L12.0752 3.94531C12.6611 3.35938 12.6611 2.42188 12.0752 1.83594ZM3.59082 10.8359L1.83301 11.1875L2.18457 9.42969L2.77051 8.84375V10.25H4.17676L3.59082 10.8359ZM3.89551 9.125V7.71875L8.02051 3.59375L9.4502 5L5.30176 9.125H3.89551Z"></path>
                                                </svg>
                                            </button>
                                        </AntTooltip>
                                        <AntTooltip
                                            content="Print Estimate"
                                            placement="bottom"
                                            getPopupContainer={() =>
                                                document.getElementById(
                                                    `print-estimate-${plan_id}`,
                                                ) || document.body
                                            }
                                        >
                                            {docProfile ? (
                                                <button
                                                    className="h-28 px-7 py-3 flex items-center space-x-6 rounded-4 bg-white border border-darwin-accent-bright-blue-800"
                                                    onClick={() => onPrint(plan_id)}
                                                    id={`print-estimate-${plan_id}`}
                                                >
                                                    {isPrintChartLoading ? (
                                                        <svg
                                                            className="w-12 h-12 fill-darwin-accent-bright-blue-800 animate-spin"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            viewBox="0 0 512 512"
                                                        >
                                                            <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
                                                        </svg>
                                                    ) : (
                                                        <svg
                                                            className="w-12 h-12 fill-darwin-accent-bright-blue-800"
                                                            viewBox="0 0 14 14"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <path d="M3.34375 4.1875H2.125V1.75C2.125 0.861328 2.83594 0.125 3.75 0.125H9.56445C9.99609 0.125 10.4023 0.302734 10.707 0.607422L11.3926 1.29297C11.6973 1.59766 11.875 2.00391 11.875 2.43555V4.1875H10.6562V2.43555C10.6562 2.33398 10.6055 2.23242 10.5293 2.15625L9.84375 1.4707C9.76758 1.39453 9.66602 1.34375 9.56445 1.34375H3.75C3.52148 1.34375 3.34375 1.54688 3.34375 1.75V4.1875ZM3.75 9.46875V11.9062H10.25V9.46875H3.75ZM3.34375 8.25H10.6562C11.0879 8.25 11.4688 8.63086 11.4688 9.0625H12.2812V6.625C12.2812 6.42188 12.0781 6.21875 11.875 6.21875H2.125C1.89648 6.21875 1.71875 6.42188 1.71875 6.625V9.0625H2.53125C2.53125 8.63086 2.88672 8.25 3.34375 8.25ZM11.4688 10.2812V12.3125C11.4688 12.7695 11.0879 13.125 10.6562 13.125H3.34375C2.88672 13.125 2.53125 12.7695 2.53125 12.3125V10.2812H1.3125C0.855469 10.2812 0.5 9.92578 0.5 9.46875V6.625C0.5 5.73633 1.21094 5 2.125 5H11.875C12.7637 5 13.5 5.73633 13.5 6.625V9.46875C13.5 9.92578 13.1191 10.2812 12.6875 10.2812H11.4688Z" />
                                                        </svg>
                                                    )}
                                                </button>
                                            ) : (
                                                <DoctorSelectorPopup
                                                    onSelect={(doc) => onPrint(plan_id, doc?.id)}
                                                >
                                                    <button
                                                        className="h-28 px-7 py-3 flex items-center space-x-6 rounded-4 bg-white border border-darwin-accent-bright-blue-800"
                                                        id={`print-estimate-${plan_id}`}
                                                    >
                                                        {isPrintChartLoading ? (
                                                            <svg
                                                                className="w-12 h-12 fill-darwin-accent-bright-blue-800 animate-spin"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                viewBox="0 0 512 512"
                                                            >
                                                                <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
                                                            </svg>
                                                        ) : (
                                                            <svg
                                                                className="w-12 h-12 fill-darwin-accent-bright-blue-800"
                                                                viewBox="0 0 14 14"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                            >
                                                                <path d="M3.34375 4.1875H2.125V1.75C2.125 0.861328 2.83594 0.125 3.75 0.125H9.56445C9.99609 0.125 10.4023 0.302734 10.707 0.607422L11.3926 1.29297C11.6973 1.59766 11.875 2.00391 11.875 2.43555V4.1875H10.6562V2.43555C10.6562 2.33398 10.6055 2.23242 10.5293 2.15625L9.84375 1.4707C9.76758 1.39453 9.66602 1.34375 9.56445 1.34375H3.75C3.52148 1.34375 3.34375 1.54688 3.34375 1.75V4.1875ZM3.75 9.46875V11.9062H10.25V9.46875H3.75ZM3.34375 8.25H10.6562C11.0879 8.25 11.4688 8.63086 11.4688 9.0625H12.2812V6.625C12.2812 6.42188 12.0781 6.21875 11.875 6.21875H2.125C1.89648 6.21875 1.71875 6.42188 1.71875 6.625V9.0625H2.53125C2.53125 8.63086 2.88672 8.25 3.34375 8.25ZM11.4688 10.2812V12.3125C11.4688 12.7695 11.0879 13.125 10.6562 13.125H3.34375C2.88672 13.125 2.53125 12.7695 2.53125 12.3125V10.2812H1.3125C0.855469 10.2812 0.5 9.92578 0.5 9.46875V6.625C0.5 5.73633 1.21094 5 2.125 5H11.875C12.7637 5 13.5 5.73633 13.5 6.625V9.46875C13.5 9.92578 13.1191 10.2812 12.6875 10.2812H11.4688Z" />
                                                            </svg>
                                                        )}
                                                    </button>
                                                </DoctorSelectorPopup>
                                            )}
                                        </AntTooltip>
                                        <button
                                            className="px-8 py-4 flex items-center space-x-6 rounded-4 bg-darwin-accent-bright-blue-800"
                                            onClick={() => onActivateFunction(treatmentPlan)}
                                        >
                                            {isLoading ? (
                                                <svg
                                                    className="w-12 h-12 fill-white animate-spin"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 512 512"
                                                >
                                                    <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
                                                </svg>
                                            ) : (
                                                <svg
                                                    className="w-12 h-10 fill-white"
                                                    viewBox="0 0 12 11"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path d="M11.4844 5.58203L7.01562 9.84766C6.76172 10.0762 6.38086 10.0762 6.15234 9.82227C5.92383 9.56836 5.92383 9.1875 6.17773 8.95898L9.55469 5.73438H0.921875C0.566406 5.73438 0.3125 5.48047 0.3125 5.125C0.3125 4.79492 0.566406 4.51562 0.921875 4.51562H9.55469L6.17773 1.31641C5.92383 1.08789 5.92383 0.681641 6.15234 0.453125C6.38086 0.199219 6.78711 0.199219 7.01562 0.427734L11.4844 4.69336C11.6113 4.82031 11.6875 4.97266 11.6875 5.125C11.6875 5.30273 11.6113 5.45508 11.4844 5.58203Z" />
                                                </svg>
                                            )}
                                            <p className="text-13 font-700 text-white">Activate</p>
                                        </button>
                                    </Fragment>
                                )}
                                {activeTab === TREATMENT_PLAN_TYPE.COMPLETED_PLAN && (
                                    <Fragment>
                                        <AntTooltip
                                            content="Work Done Sheet"
                                            placement="bottom"
                                            getPopupContainer={() =>
                                                document.getElementById(
                                                    `work-done-sheet-${plan_id}`,
                                                ) || document.body
                                            }
                                        >
                                            <button
                                                className="p-6 pr-2"
                                                onClick={() =>
                                                    treatmentBillingModalToggleHandler(
                                                        treatmentPlan,
                                                        true,
                                                    )
                                                }
                                                id={`work-done-sheet-${plan_id}`}
                                            >
                                                <svg
                                                    viewBox="0 0 11 13"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    className="w-10 h-14 fill-darwin-neutral-900"
                                                >
                                                    <path
                                                        d="M10.5625 11.0234L8.28906 8.77344L10.5391 6.52344C10.7734 6.28906 10.7734 5.9375 10.5391 5.72656L10.0234 5.1875C9.78906 4.97656 9.4375 4.97656 9.22656 5.1875L7 7.4375L5.40625 5.84375C6.50781 5.32812 7.21094 4.08594 6.92969 2.72656C6.64844 1.41406 5.42969 0.5 4.09375 0.5H0.976562C0.578125 0.5 0.25 0.851562 0.25 1.27344V7.46094C0.25 7.76562 0.484375 8.02344 0.789062 8.02344H1.51562C1.82031 8.02344 2.05469 7.76562 2.05469 7.46094V6.14844H3.03906L5.66406 8.77344L3.41406 11.0234C3.20312 11.2344 3.20312 11.5859 3.41406 11.8203L3.95312 12.3594C4.16406 12.5703 4.51562 12.5703 4.72656 12.3594L7 10.0859L9.25 12.3594C9.46094 12.5703 9.8125 12.5703 10.0469 12.3594L10.5625 11.8203C10.7969 11.5859 10.7969 11.2344 10.5625 11.0234ZM2.10156 2.39844H4.14062C4.65625 2.39844 5.05469 2.82031 5.05469 3.33594C5.05469 3.85156 4.63281 4.27344 4.14062 4.27344H2.10156V2.39844Z"
                                                        fill="#1A1A1A"
                                                    ></path>
                                                </svg>
                                            </button>
                                        </AntTooltip>
                                        <AntTooltip
                                            content="Treatment Billing"
                                            placement="bottom"
                                            getPopupContainer={() =>
                                                document.getElementById(
                                                    `treatment-billing-${plan_id}`,
                                                ) || document.body
                                            }
                                        >
                                            <button
                                                className="p-6"
                                                onClick={() =>
                                                    treatmentBillingModalToggleHandler(
                                                        treatmentPlan,
                                                    )
                                                }
                                                id={`treatment-billing-${plan_id}`}
                                            >
                                                <svg
                                                    className="w-10 h-14"
                                                    viewBox="0 0 10 14"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path
                                                        d="M1.11523 0.277344L2.15625 1.16602L3.17188 0.277344C3.40039 0.0996094 3.73047 0.0996094 3.95898 0.277344L5 1.16602L6.01562 0.277344C6.24414 0.0996094 6.57422 0.0996094 6.80273 0.277344L7.84375 1.16602L8.85938 0.277344C9.03711 0.125 9.29102 0.0996094 9.51953 0.201172C9.72266 0.302734 9.875 0.505859 9.875 0.734375V12.5156C9.875 12.7695 9.72266 12.9727 9.51953 13.0742C9.29102 13.1758 9.03711 13.1504 8.85938 12.998L7.84375 12.1094L6.80273 12.998C6.57422 13.1758 6.24414 13.1758 6.01562 12.998L5 12.1094L3.95898 12.998C3.73047 13.1758 3.40039 13.1758 3.17188 12.998L2.15625 12.1094L1.11523 12.998C0.9375 13.1504 0.683594 13.1758 0.480469 13.0742C0.251953 12.9727 0.125 12.7695 0.125 12.5156V0.734375C0.125 0.505859 0.251953 0.302734 0.480469 0.201172C0.683594 0.0996094 0.9375 0.125 1.11523 0.277344ZM1.34375 11.1953L1.75 10.8398C1.97852 10.6621 2.30859 10.6621 2.53711 10.8398L3.57812 11.7285L4.59375 10.8398C4.82227 10.6621 5.15234 10.6621 5.38086 10.8398L6.42188 11.7285L7.4375 10.8398C7.66602 10.6621 7.99609 10.6621 8.22461 10.8398L8.65625 11.1953V2.08008L8.22461 2.43555C7.99609 2.61328 7.66602 2.61328 7.4375 2.43555L6.42188 1.54688L5.38086 2.43555C5.15234 2.61328 4.82227 2.61328 4.59375 2.43555L3.57812 1.54688L2.53711 2.43555C2.30859 2.61328 1.97852 2.61328 1.75 2.43555L1.34375 2.08008V11.1953ZM3.17188 3.78125H6.82812C7.1582 3.78125 7.4375 4.06055 7.4375 4.39062C7.4375 4.74609 7.1582 5 6.82812 5H3.17188C2.81641 5 2.5625 4.74609 2.5625 4.39062C2.5625 4.06055 2.81641 3.78125 3.17188 3.78125ZM3.17188 8.25H6.82812C7.1582 8.25 7.4375 8.5293 7.4375 8.85938C7.4375 9.21484 7.1582 9.46875 6.82812 9.46875H3.17188C2.81641 9.46875 2.5625 9.21484 2.5625 8.85938C2.5625 8.5293 2.81641 8.25 3.17188 8.25ZM2.5625 6.625C2.5625 6.29492 2.81641 6.01562 3.17188 6.01562H6.82812C7.1582 6.01562 7.4375 6.29492 7.4375 6.625C7.4375 6.98047 7.1582 7.23438 6.82812 7.23438H3.17188C2.81641 7.23438 2.5625 6.98047 2.5625 6.625Z"
                                                        fill="#1A1A1A"
                                                    />
                                                </svg>
                                            </button>
                                        </AntTooltip>
                                        <button className="px-8 py-4 flex items-center space-x-6 rounded-4 bg-darwin-green-primary">
                                            <svg
                                                className="w-14 h-14 fill-white"
                                                viewBox="0 0 14 14"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path d="M7 13.625C4.66406 13.625 2.53125 12.4062 1.36328 10.375C0.195312 8.36914 0.195312 5.90625 1.36328 3.875C2.53125 1.86914 4.66406 0.625 7 0.625C9.31055 0.625 11.4434 1.86914 12.6113 3.875C13.7793 5.90625 13.7793 8.36914 12.6113 10.375C11.4434 12.4062 9.31055 13.625 7 13.625ZM9.86914 5.93164H9.84375C10.0977 5.70312 10.0977 5.32227 9.84375 5.06836C9.61523 4.83984 9.23438 4.83984 9.00586 5.06836L6.1875 7.91211L4.99414 6.71875C4.74023 6.46484 4.35938 6.46484 4.13086 6.71875C3.87695 6.94727 3.87695 7.32812 4.13086 7.55664L5.75586 9.18164C5.98438 9.43555 6.36523 9.43555 6.61914 9.18164L9.86914 5.93164Z" />
                                            </svg>
                                            <p className="text-13 font-700 text-white">
                                                Completed on{' '}
                                                {moment(updated_at).format("DD MMM'YY")}
                                            </p>
                                        </button>
                                    </Fragment>
                                )}
                            </div>
                        }
                    >
                        <div
                            className="space-y-16 popup-overlay-show padV2-treatment-plan"
                            id={`estimate-container-${plan_id}`}
                        >
                            <TreatmentServicesTable services={services} />
                            <TreatmentPricing treatmentPlan={treatmentPlan} />
                        </div>
                    </Panel>
                </Collapse>
            </Space>
        );
    }
}

export default ActiveTabPlan;
